import React, { useState, useEffect, useContext, useRef } from 'react'
import ReactDOM from 'react-dom'
import ModalWindow from '../../layout/modalwindow/ModalWindow'
import InvoiceHeader from './InvoiceHeader'
import InvoiceInner from './InvoiceInner'
import InvoiceFooter from './InvoiceFooter'
import InvoiceListController from '../invoicelist/InvoiceListController'
import { showMenu } from '../../layout/toolbar/Toolbar'
import { appContext } from '../../../App'
import { dateYMD } from '../../utilities/utilities'
import { deleteInvoice, getCurrentVAT, getProductsList, getItemInventory, getWarehouses, getInvoiceById,
  saveInvoice, getInvoiceDetailsFromCustomersAccounts, updateItemQuantity } from '../InvoiceFunctions'
import { isObjEmpty } from '../../utilities/utilities'

export const invoiceControllerContext = React.createContext()

function useInvoiceController(props) {
  const app = useContext(appContext)
  const [warehouses, setWarehouses] = useState(null)
  const setContent = app.content[1]
  const invoiceRef = useRef(null)

  // It's important that it appears before invoice, setInvoice
  const initInvoice = {
    //invoice_date: dateYMD(new Date(app.getClientDateTime())),
    invoice_date: dateYMD(new Date()),
    warehouse: null,
    prepared_by: null,
    remarks: null,
    status: 'On Process',
    customer_details: {
      sold_to: 'Walked-in Customer',
      address: '',
      contact_number: '',
      remarks: '',
      rewards_points: 0.00
    },
    invoice_particulars: [],
    vat: 0,
    invoice_summary: {
      total_sales: 0.00,
      none_vat_total_sales: 0.00,
      none_vat_total_items: 0.00,
      vat_total_sales: 0.00,
      vat_amount: 0.00,
      vat_total_items: 0.00,
      total_item: 0,
    }
  }

  let [invoice, setInvoice] = useState(null)
  const [customersDetails, setCustomersDetails] = useState([])
  const [productsList, setProductsList] = useState(null)
  const [pulledOutItemHistory, setPulledOutItemHistory] = useState([])
  const [restorePerfect, setRestorePerfect] = useState(true)

  // Session Timer
  const [timerDisplay, setTimerDisplay] = useState('')
  // 30 minutes of inactivity will set invoice status 'Inactive'
  const sessionTime = { hours: 0, minutes: 30, seconds: 0, milliseconds: 0 }
  let timer = useRef(new Date()) // Invoice session timer
  let timerID = useRef(null)
  const resetTimer = useRef(false)

  const updateTimerDisplay = async () => {
    const showMessageBox = (message) => {
      ReactDOM.render (
        <ModalWindow
          id='mw-parent'
          header="Invoice Session Expires"
          footer={null}>
          <div className='messagebox'>
            <div className='messagebox-content'>
              <div>{ message }</div>
              <br/>
            </div>
            <div className='button-container'>
              <button
                onClick={() => {
                  setContent(<InvoiceListController />)
                  ReactDOM.unmountComponentAtNode(document.getElementById('modal-form-container'))
                }}
                className='pospro-action-btn top-anchor'>
                Ok
              </button>
            </div>
          </div>
        </ModalWindow>,
        document.getElementById('modal-form-container')
      )
    }

    if (resetTimer.current) {
      resetTimer.current = false
      timer.current.setHours(sessionTime.hours, sessionTime.minutes, sessionTime.seconds, sessionTime.milliseconds)
    }

    const sessionExpires = (!(timer.current.getHours() + timer.current.getMinutes() + timer.current.getSeconds()))

    timer.current.setSeconds(timer.current.getSeconds() - 1)

    if (!sessionExpires)
      setTimerDisplay(
        <React.Fragment>
          <span className='session-label'>Session will expire in</span>&nbsp;
          <span className='session-timer'><b>{timer.current.getHours()}:{timer.current.getMinutes()}:{timer.current.getSeconds()}</b></span>
        </React.Fragment>
      )
    else {
      clearInterval(timerID.current)
      setTimerDisplay(<span className='session-label'>Invoice sessions expires!</span>)

      // Set it to inactive if sessions expires provided that it has particulars
      if (invoiceRef.current.invoice_particulars.length > 0) {
        invoiceRef.current.status = 'Inactive'
        await saveInvoice(app, invoiceRef.current)
      }

      showMessageBox('Your invoice session expires!')
    }
  }

  const addPulledOutItemHistory = (item) => {
    if (pulledOutItemHistory)
      setPulledOutItemHistory([ ...pulledOutItemHistory, item ])
    else
      setPulledOutItemHistory([item])
  }

  const reCalcInvoiceSummary = () => { calcInvoiceSummary() }

  const calcInvoiceSummary = () => {
    if (invoice.invoice_particulars.length === 0) {
      invoice.invoice_summary = initInvoice.invoice_summary
      return
    }

    // Compute Invoice Total Sales
    const invoiceTotalSales =
    parseFloat (
      invoice.invoice_particulars
      .map(item => item.amount)
      .reduce((cur, prev) => cur + prev).toFixed(2)
    )

    let invoiceNoneVATTotalSales = 0.00
    const noneVatableItems = invoice.invoice_particulars.filter(ip => (!ip.vatable))
    if (noneVatableItems.length > 0) {
      invoiceNoneVATTotalSales = noneVatableItems
      .map(item => item.amount)
      .reduce((cur, prev) => cur + prev).toFixed(2)
    }

    let invoiceVATTotalSales = 0.00
    const vatableItems = invoice.invoice_particulars.filter(ip => ip.vatable)
    if (vatableItems.length > 0) {
      invoiceVATTotalSales = vatableItems
      .map(item => item.amount)
      .reduce((cur, prev) => cur + prev).toFixed(2)
    }

    const totalNetProfit =
    parseFloat (
      invoice.invoice_particulars
      .map(item => item.net_profit)
      .reduce((cur, prev) => cur + prev).toFixed(2)
    )

    const invoiceVATAmount = parseFloat((invoiceVATTotalSales - (invoiceVATTotalSales / (1 + (invoice.vat / 100)))).toFixed(2))

    invoice.invoice_summary = {
      ...invoice.invoice_summary,
      total_sales: invoiceTotalSales,
      none_vat_total_sales: parseFloat(invoiceNoneVATTotalSales),
      none_vat_total_items: noneVatableItems.length,
      vat_total_sales: parseFloat(invoiceVATTotalSales),
      vat_amount: invoiceVATAmount,
      vat_total_items: vatableItems.length,
      total_item: invoice.invoice_particulars.length,
      total_net_profit: parseFloat(totalNetProfit)
    }
  }

  const resetInvoice = async () => {
    // Restore all items on the invoice back to the inventory
    for (let item of invoice.invoice_particulars) {
      await updateItemQuantity(app, invoice.warehouse.id, { mode: 1, ip: item })
    }
    setInvoice({ ...invoice, ...initInvoice, warehouse: invoice.warehouse })
  }

  const restorePulledOutItems = async (item=null) => {
    // If it's a Reset call
    if (pulledOutItemHistory && !item) {
      if (pulledOutItemHistory.length > 0) {
        for (let item of pulledOutItemHistory) {
          // Restoring back for this item
          // First make sure that this item restore process will result no negative item quantity
          // Check if it's ok to restore back the item
          const ip = {item_id: item.item_id, ...item.units.find(itemUnit => itemUnit.mode === 1)}
          const ipInventory = await getItemInventory(app, invoice.warehouse.id, ip)
          const difference = (ipInventory.inventory - ip.quantity)
          const restoreItemOK = (difference >= 0)

          setRestorePerfect(restorePerfect && restoreItemOK)

          // It's ok to restore back
          if (restoreItemOK) {
            for (let itemUnits of item.units) {
              const ip = { item_id: item.item_id, ...itemUnits }
              await updateItemQuantity(app, invoice.warehouse.id, { mode: (itemUnits.mode * -1), ip })
            }
            // Reset pulledOutItemHistory
            setPulledOutItemHistory([])
          } else { }
        }
      }
    } else if (pulledOutItemHistory && item) {
      // If it's a delete or edit individual invoice particular call, then
      if (pulledOutItemHistory.length > 0) {
        for (let itemPulledOutIndex in pulledOutItemHistory) {
          const itemToRestore = pulledOutItemHistory[itemPulledOutIndex]
          // Find the item deleted on the pulledouthistory record
          // It's like &&'ing' it too
          if (itemToRestore.item_id === item.item_id)
            if (itemToRestore.units.find(unit => ((unit.unit === item.unit) && (unit.mode === 1)))) {

              // Check if it's ok to restore this item
              const ip = {item_id: item.item_id, ...itemToRestore.units.find(unit => unit.mode === 1)}
              const ipInventory = await getItemInventory(app, invoice.warheouse.id, ip)
              const difference = (ipInventory.inventory - ip.quantity)
              const restoreItemOK = (difference >= 0)

              setRestorePerfect(restorePerfect && restoreItemOK)

              // Restore here
              if (restoreItemOK) {
                for (let unit of itemToRestore.units) {
                  const ip = { item_id: itemToRestore.item_id, ...unit }
                  // Reverse the action
                  await updateItemQuantity(app, invoice.warehouse.id, { mode: (unit.mode * -1), ip })
                }
              } else { }
              // Delete it after restoring only if it's not in invoice particular delete mode
              pulledOutItemHistory.splice(itemPulledOutIndex, 1)
            }
        }
      } else { }
    }
  }

  const reloadDataSets = async (silent=false) => {
    const products_list = await getProductsList(app, invoice.warehouse.id, silent)
    setProductsList(products_list.map(item => (
      {
        ...item,
        item_full_description: `${item.brand} ${item.name} ${item.description}`
      }
    )))
    setCustomersDetails(await getInvoiceDetailsFromCustomersAccounts(app, silent))
  }
  
  useEffect(() => {
    const init = async () => {
      const whs = await getWarehouses(app)
      setWarehouses(whs)

      if (isObjEmpty(props))
        setInvoice({
          ...initInvoice,
          vat: await getCurrentVAT(app),
          warehouse: { name: whs[0].name, id: whs[0]._id }
        })
      else
        setInvoice({
          ...props.invoice,
          status: 'On Process',
          invoice_date: dateYMD(new Date(props.invoice.invoice_date))
        })

      // timer starts
      timer.current.setHours(sessionTime.hours, sessionTime.minutes, sessionTime.seconds, sessionTime.milliseconds)
      // Session timer starts
      timerID.current = setInterval(updateTimerDisplay, 1000)
      // Auto-hide the sidebar menu here
      showMenu(false)
    }

    init()

    return (async () => {
      clearInterval(timerID.current)
      //icWillUnmount.current = true
      if ((invoiceRef.current.invoice_particulars.length === 0) && (invoiceRef.current.status === 'On Process')) {
        await deleteInvoice(app, invoiceRef.current._id)
      }
    })

    /* Hack to avoid the eslint warning */
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const autoSaveInvoice = async () => {
      // It's an update
      if (invoice._id) {
        if (invoice.new) delete invoice.new
        else {
          calcInvoiceSummary()
          await saveInvoice(app, invoice, true)
          reloadDataSets(true) // silent mode (don't show loading indicator)
        }

        invoiceRef.current = invoice
        resetTimer.current = true
      }
      // It's new invoice
      else {
        const inv = await saveInvoice(app, invoice)
        setInvoice({
          ...inv,
          new: true,
          invoice_date: dateYMD(new Date(inv.invoice_date))
        })
        reloadDataSets() // first time reload (show loading indicator)
      }
    }

    if (invoice) autoSaveInvoice()

    /* Hack to avoid the eslint warning */
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice])
  
  return ({
    invoice: [invoice, setInvoice],
    timerDisplay: [timerDisplay, setTimerDisplay],
    reCalcInvoiceSummary,
    warehouses,
    resetInvoice,
    restorePulledOutItems,
    saveInvoice,
    getInvoiceById,
    customersDetails,
    productsList,
    updateItemQuantity,
    addPulledOutItemHistory,
    reloadDataSets,
    getItemInventory,
    restorePerfect
  })
}

function InvoiceController(props) {
  const invoiceController = useInvoiceController(props)

  if (invoiceController.invoice[0])
    return (
      <React.Fragment>
        <invoiceControllerContext.Provider value={invoiceController}>
          <InvoiceHeader />
          <InvoiceInner />
          <InvoiceFooter />
        </invoiceControllerContext.Provider>
      </React.Fragment>
    )
  else return null
}

export default React.memo(InvoiceController)